import { useState, useEffect, useRef } from "react";
import { useStudioEditor } from "../../../store/studioEditorStore";
import { useStudioErstellen } from "../../../store/studioErstellenStore";

import classes from "../../StudioErstellen/Preis.module.css";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";
import offerIcon from "../../../public/studioErstellen/percentageImage.webp";
import Stundenpreis from "../../StudioErstellen/price/Stundenpreis";
import Paketpreise from "../../StudioErstellen/price/Paketpreise";
import { buildPricingUpdateFields } from "./pricingUpdateFields";

const createPricingDataSnapshot = (data) => JSON.stringify({
    pricingType: data?.pricingType,
    packages: data?.packages,
    priceGroups: data?.priceGroups,
    adjustPriceByPeople: data?.adjustPriceByPeople,
    pricePerHour: data?.pricePerHour,
    minNumberOfHours: data?.minNumberOfHours,
    discounts: data?.discounts,
    fees: data?.fees,
    weekendSurcharge: data?.weekendSurcharge,
    weekendDays: data?.weekendDays,
    weekendMode: data?.weekendMode,
    productionAreaSize: data?.productionAreaSize,
    capacity: data?.capacity
});

const PRICING_FIELD_NAMES = [
    'pricingMode',
    'pricePerHour',
    'minNumberOfHours',
    'tieredPricing',
    'packages',
    'discountRules',
    'extraFees',
    'weekendSurcharge',
    'productionAreaSize',
    'numberOfPeople',
];

export const PreisEditor = ({ validate, studio }) => {
    const { updateField, updatedFields } = useStudioEditor();
    const { studioData: editorStudioData, updateStudioData } = useStudioErstellen();

    // Check if studio has new pricing structure
    // Treat `pricingMode: null` as "new pricing with default HOURLY"
    // Only studios where `pricingMode` is truly undefined use the old simple pricing UI.
    const hasNewPricing = studio?.pricingMode !== undefined;
    // For backward compatibility: simple hourly pricing
    const [formData, setFormData] = useState({
        pricePerHour: "",
        minNumberOfHours: ""
    });

    const [errors, setErrors] = useState({
        pricePerHour: "",
        minNumberOfHours: "",
    });

    // For new pricing structure: use studioErstellenStore format
    const [selectedPricingType, setSelectedPricingType] = useState(null);
    const childValidateRef = useRef(null);
    const isInitializingRef = useRef(false);
    const lastBackendDataHashRef = useRef(null); // Hash of backend data for transformation
    const lastSyncedDataRef = useRef(null); // Snapshot of frontend data for sync
    const latestPricingDataRef = useRef(null);
    const latestPricingSnapshotRef = useRef(null);

    // Transform backend data to frontend format for new pricing
    useEffect(() => {
        if (!studio || !hasNewPricing) return;

        const hasPricingUpdates = PRICING_FIELD_NAMES.some((fieldName) =>
            Object.prototype.hasOwnProperty.call(updatedFields, fieldName)
        );
        const sourceStudio = hasPricingUpdates
            ? PRICING_FIELD_NAMES.reduce(
                (acc, fieldName) => (
                    Object.prototype.hasOwnProperty.call(updatedFields, fieldName)
                        ? { ...acc, [fieldName]: updatedFields[fieldName] }
                        : acc
                ),
                { ...studio }
            )
            : studio;

        // Create a stable hash of the pricing data to detect changes
        const pricingDataHash = JSON.stringify({
            id: sourceStudio.id,
            pricingMode: sourceStudio.pricingMode,
            pricePerHour: sourceStudio.pricePerHour,
            minNumberOfHours: sourceStudio.minNumberOfHours,
            tieredPricing: sourceStudio.tieredPricing,
            packages: sourceStudio.packages,
            discountRules: sourceStudio.discountRules,
            extraFees: sourceStudio.extraFees,
            weekendSurcharge: sourceStudio.weekendSurcharge,
            numberOfPeople: sourceStudio.numberOfPeople,
            productionAreaSize: sourceStudio.productionAreaSize
        });

        // Only transform if data actually changed
        if (lastBackendDataHashRef.current === pricingDataHash) {
            return;
        }

        isInitializingRef.current = true;

        // For HOURLY mode: get priceGroups from tieredPricing
        let adjustPriceByPeople = sourceStudio.tieredPricing && sourceStudio.tieredPricing.length > 0;
        let priceGroups = sourceStudio.tieredPricing ? sourceStudio.tieredPricing.map(tier => ({
            id: Date.now() + Math.random(),
            from: tier.minPersons?.toString() || '1',
            to: tier.maxPersons?.toString() || (sourceStudio.numberOfPeople?.toString() || '20'),
            price: tier.pricePerHour?.toString() || ''
        })) : [];

        // For PACKAGES mode: extract priceGroups from packages' prices if they have multiple person groups
        const sourcePricingMode = sourceStudio.pricingMode || 'HOURLY';
        const sourceIsPackagesMode = sourcePricingMode === 'PACKAGES';
        if (sourceIsPackagesMode && sourceStudio.packages && sourceStudio.packages.length > 0) {
            // Collect all unique person groups from all packages
            const personGroupsMap = new Map();

            sourceStudio.packages.forEach(pkg => {
                if (pkg.prices && Array.isArray(pkg.prices) && pkg.prices.length > 1) {
                    // Multiple price groups found - extract them
                    pkg.prices.forEach(priceEntry => {
                        const key = `${priceEntry.minPersons}-${priceEntry.maxPersons}`;
                        if (!personGroupsMap.has(key)) {
                            personGroupsMap.set(key, {
                                minPersons: priceEntry.minPersons,
                                maxPersons: priceEntry.maxPersons
                            });
                        }
                    });
                }
            });

            // If we found multiple person groups, create priceGroups from them
            if (personGroupsMap.size > 1) {
                adjustPriceByPeople = true;
                priceGroups = Array.from(personGroupsMap.values())
                    .sort((a, b) => a.minPersons - b.minPersons)
                    .map((group, index) => ({
                        id: Date.now() + index + Math.random(),
                        from: group.minPersons?.toString() || '1',
                        to: group.maxPersons?.toString() || (sourceStudio.numberOfPeople?.toString() || '20'),
                        price: '' // Price groups in packages mode don't have prices themselves
                    }));
            }
        }

        const transformedData = {
            pricingType: sourceIsPackagesMode ? 'paketpreise' : 'stundenpreis',
            pricePerHour: sourceStudio.pricePerHour || '',
            minNumberOfHours: sourceStudio.minNumberOfHours || '',
            adjustPriceByPeople: adjustPriceByPeople,
            priceGroups: priceGroups,
            packages: sourceStudio.packages ? sourceStudio.packages.map(pkg => {
                // Transform package prices from array to object format
                // Need to match priceGroups structure - if we have priceGroups, use their IDs
                // Otherwise use 'all' as the key
                const pricesObj = {};
                const hasPriceGroups = adjustPriceByPeople && priceGroups.length > 0;

                if (pkg.prices && Array.isArray(pkg.prices)) {
                    if (hasPriceGroups) {
                        // Match prices to priceGroups by min/max persons
                        pkg.prices.forEach(priceGroup => {
                            const matchingGroup = priceGroups.find(g =>
                                parseInt(g.from) === priceGroup.minPersons &&
                                parseInt(g.to) === priceGroup.maxPersons
                            );
                            if (matchingGroup) {
                                pricesObj[matchingGroup.id] = priceGroup.price?.toString() || '';
                            }
                        });
                    } else {
                        // No price groups - use 'all' key
                        if (pkg.prices.length > 0) {
                            pricesObj['all'] = pkg.prices[0].price?.toString() || '';
                        }
                    }
                }
                return {
                    id: pkg.id || Date.now() + Math.random(),
                    name: pkg.name || `${pkg.durationHours} ${pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`,
                    hours: pkg.durationHours?.toString() || '1',
                    prices: pricesObj
                };
            }) : [],
            discounts: sourceStudio.discountRules ? sourceStudio.discountRules.map(rule => {
            // Normalize backend discount type to the values the editor expects:
            // - 'PERCENTAGE'  -> 'percentage'
            // - 'FIXED' (or anything else) -> 'amount'
            const normalizedType =
                rule.discountType === 'PERCENTAGE'
                ? 'percentage'
                : 'amount';

            return {
                id: Date.now() + Math.random(),
                minHours: rule.minHours?.toString() || '8',
                discountType: normalizedType,
                discountValue: rule.discountValue?.toString() || '',
                basePrice: '0'
            };
            }) : [],
            fees: sourceStudio.extraFees ? sourceStudio.extraFees.map(fee => ({
                id: fee.id || Date.now() + Math.random(),
                description: fee.name || '',
                amount: fee.amount?.toString() || ''
            })) : [],
            weekendSurcharge: sourceStudio.weekendSurcharge ? (
                sourceStudio.weekendSurcharge.saturdayEnabled || sourceStudio.weekendSurcharge.sundayEnabled
            ) : false,
            weekendDays: (() => {
                const days = [];
                if (sourceStudio.weekendSurcharge?.saturdayEnabled) {
                    days.push({
                        day: 'Samstag',
                        percent: sourceStudio.weekendSurcharge.saturdaySurchargeValue?.toString() || '20'
                    });
                }
                if (sourceStudio.weekendSurcharge?.sundayEnabled) {
                    days.push({
                        day: 'Sonntag',
                        percent: sourceStudio.weekendSurcharge.sundaySurchargeValue?.toString() || '20'
                    });
                }
                return days;
            })(),
            weekendMode: (() => {
                // Check both Saturday and Sunday to determine mode
                const saturdayType = sourceStudio.weekendSurcharge?.saturdaySurchargeType;
                const sundayType = sourceStudio.weekendSurcharge?.sundaySurchargeType;
                // Use the type from whichever day is enabled, defaulting to Saturday if both are enabled
                const surchargeType = saturdayType || sundayType;
                return surchargeType === 'PERCENTAGE' ? 'percentage' : 'amount';
            })(),
            capacity: sourceStudio.numberOfPeople?.toString() || '20',
            productionAreaSize: sourceStudio.productionAreaSize || ''
        };
        const transformedSnapshot = createPricingDataSnapshot(transformedData);
        const hasEditorDraftForStudio = editorStudioData?.pricingEditorStudioId === studio.id;
        const hasUnsavedEditorDraft =
            hasEditorDraftForStudio &&
            editorStudioData?.pricingEditorBaseSnapshot &&
            createPricingDataSnapshot(editorStudioData) !== editorStudioData.pricingEditorBaseSnapshot;

        if (!hasPricingUpdates && hasUnsavedEditorDraft) {
            lastBackendDataHashRef.current = pricingDataHash;
            lastSyncedDataRef.current = createPricingDataSnapshot(editorStudioData);
            isInitializingRef.current = false;
            return;
        }

        setSelectedPricingType(transformedData.pricingType);

        // Update studioErstellenStore with transformed data
        Object.keys(transformedData).forEach(key => {
            updateStudioData(key, transformedData[key]);
        });
        updateStudioData('pricingEditorStudioId', studio.id);
        updateStudioData('pricingEditorBaseSnapshot', transformedSnapshot);

        // Store hash for comparison
        lastBackendDataHashRef.current = pricingDataHash;
        lastSyncedDataRef.current = transformedSnapshot;
        isInitializingRef.current = false;
    }, [studio, hasNewPricing, updatedFields, editorStudioData, updateStudioData]); // Re-run when studio data changes

    // For backward compatibility: simple hourly pricing
    useEffect(() => {
        if (studio && !hasNewPricing) {
            const priceValue = updatedFields.pricePerHour ?? studio.pricePerHour ?? "";
            const minNumberOfHoursValue = updatedFields.minNumberOfHours ?? studio.minNumberOfHours ?? "";

            setFormData({
                pricePerHour: priceValue,
                minNumberOfHours: minNumberOfHoursValue
            });
        }
        // Only hydrate local state on studio changes. User inputs update the editor store.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio, hasNewPricing]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow positive integers (remove any non-digit characters)
        const sanitizedValue = value.replace(/[^0-9]/g, '');

        if (name === 'pricePerHour') {
            setFormData(prev => ({
                ...prev,
                pricePerHour: sanitizedValue
            }));

            if (sanitizedValue && parseInt(sanitizedValue) >= 1) {
                updateField('pricePerHour', sanitizedValue);
                setErrors(prev => ({...prev, pricePerHour: ""}));
            } else if (sanitizedValue === "") {
                setErrors(prev => ({...prev, pricePerHour: "Preis muss ausgefüllt werden."}));
            }
        } else if (name === 'minNumberOfHours') {
            setFormData(prev => ({
                ...prev,
                minNumberOfHours: sanitizedValue
            }));

            if (sanitizedValue && parseInt(sanitizedValue) >= 1) {
                updateField('minNumberOfHours', sanitizedValue);
                setErrors(prev => ({...prev, minNumberOfHours: ""}));
            } else if (sanitizedValue === "") {
                setErrors(prev => ({...prev, minNumberOfHours: "Mindeststundenanzahl muss ausgefüllt werden"}));
            }
        }
    };

    const handlePricingTypeChange = (type) => {
        setSelectedPricingType(type);
        updateStudioData('pricingType', type);

        // When switching modes, clear the old mode's data but keep shared data (discounts, fees, weekend surcharge)
        if (type === 'paketpreise') {
            // Switching to PACKAGES - clear HOURLY-specific data
            updateStudioData('minNumberOfHours', '');
            updateStudioData('pricePerHour', '');
            updateStudioData('adjustPriceByPeople', false);
            updateStudioData('priceGroups', []);
            updateStudioData('packages', []); // Will be populated by user
        } else {
            // Switching to HOURLY - clear PACKAGES-specific data
            updateStudioData('packages', []);
            // Keep pricePerHour and minNumberOfHours as they might have been set
        }
        // Keep discounts, fees, weekendSurcharge, weekendDays as they apply to both modes
    };

    const handleKeyDown = (e) => {
        // Prevent: decimal point, comma, minus, plus, 'e', 'E'
        if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
            e.preventDefault();
        }
    };

    // Validation for backward compatible simple pricing
    const handleSimpleValidation = () => {
        let isValid = true;
        const newErrors = {
            pricePerHour: "",
            minNumberOfHours: "",
        };

        // Preis nur auf "ausgefüllt" prüfen, min/max erfolgt über Input-Attribute
        if (!formData.pricePerHour) {
            newErrors.pricePerHour = "Preis muss ausgefüllt werden.";
            isValid = false;
        }

        if (!formData.minNumberOfHours || formData.minNumberOfHours < 1) {
            newErrors.minNumberOfHours = "Mindestens 1 Stunde.";
            isValid = false;

            if (studio && studio.minNumberOfHours) {
                setTimeout(() => {
                    setFormData(prev => ({
                        ...prev,
                        minNumberOfHours: studio.minNumberOfHours
                    }));
                    setErrors(prev => ({...prev, minNumberOfHours: ""}));
                }, 1500);
            }
        }

        if (formData.minNumberOfHours > 12) {
            newErrors.minNumberOfHours = "Maximal 12 Stunden.";
            isValid = false;

            if (studio && studio.minNumberOfHours) {
                setTimeout(() => {
                    setFormData(prev => ({
                        ...prev,
                        minNumberOfHours: studio.minNumberOfHours
                    }));
                    setErrors(prev => ({...prev, minNumberOfHours: ""}));
                }, 1500);
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    // Validation for new pricing structure
    const handleNewPricingValidation = () => {
        if (!selectedPricingType) {
            return false;
        }

        if (childValidateRef.current) {
            return childValidateRef.current();
        }

        return true;
    };

    useEffect(() => {
        if (validate && validate.current) {
            if (hasNewPricing) {
                validate.current = handleNewPricingValidation;
            } else {
                validate.current = handleSimpleValidation;
            }
        }
    }, [validate, formData, hasNewPricing, selectedPricingType]);

    // Sync editorStudioData changes back to updatedFields for saving (debounced to prevent infinite loops)
    useEffect(() => {
        if (!hasNewPricing || isInitializingRef.current || !editorStudioData) {
            return;
        }

        // Create a snapshot of current data for comparison
        const currentDataSnapshot = createPricingDataSnapshot(editorStudioData);
        latestPricingDataRef.current = editorStudioData;
        latestPricingSnapshotRef.current = currentDataSnapshot;

        // Only sync if data actually changed
        if (currentDataSnapshot === lastSyncedDataRef.current) {
            return;
        }

        // Debounce: wait a bit before syncing to avoid too frequent updates
        const timeoutId = setTimeout(() => {
            const backendData = buildPricingUpdateFields(editorStudioData);

            Object.keys(backendData).forEach(key => {
                if (backendData[key] !== undefined) {
                    updateField(key, backendData[key]);
                }
            });

            // Update last synced data
            lastSyncedDataRef.current = currentDataSnapshot;
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [editorStudioData?.pricingType, editorStudioData?.packages, editorStudioData?.priceGroups, editorStudioData?.adjustPriceByPeople, editorStudioData?.pricePerHour, editorStudioData?.minNumberOfHours, editorStudioData?.discounts, editorStudioData?.fees, editorStudioData?.weekendSurcharge, editorStudioData?.weekendDays, editorStudioData?.weekendMode, editorStudioData?.productionAreaSize, editorStudioData?.capacity, hasNewPricing, updateField]);

    useEffect(() => {
        return () => {
            const latestSnapshot = latestPricingSnapshotRef.current;
            const latestData = latestPricingDataRef.current;
            if (!hasNewPricing || !latestData || !latestSnapshot || latestSnapshot === lastSyncedDataRef.current) {
                return;
            }

            const backendData = buildPricingUpdateFields(latestData);
            Object.keys(backendData).forEach(key => {
                if (backendData[key] !== undefined) {
                    updateField(key, backendData[key]);
                }
            });
            lastSyncedDataRef.current = latestSnapshot;
        };
    }, [hasNewPricing, updateField]);

    const CheckIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="12" fill="#1F1F1F"/>
            <line x1="6.68079" y1="11.8187" x2="10.2545" y2="15.4692" stroke="white" strokeWidth="1.55556"/>
            <line x1="9.16709" y1="15.4508" x2="17.5924" y2="7.43704" stroke="white" strokeWidth="1.55556"/>
        </svg>
    );

    // Handle upgrade from old pricing to new pricing system
    const handleUpgradeToNewPricing = () => {
        // Initialize with current simple pricing data
        const initialData = {
            pricingType: 'stundenpreis', // Default to hourly
            pricePerHour: formData.pricePerHour || studio?.pricePerHour || '',
            minNumberOfHours: formData.minNumberOfHours || studio?.minNumberOfHours || '',
            adjustPriceByPeople: false,
            priceGroups: [],
            packages: [],
            discounts: [],
            fees: [],
            weekendSurcharge: false,
            weekendDays: [],
            weekendMode: 'percentage',
            capacity: studio?.numberOfPeople?.toString() || '20',
            productionAreaSize: ''
        };

        setSelectedPricingType('stundenpreis');
        Object.keys(initialData).forEach(key => {
            updateStudioData(key, initialData[key]);
        });

        // Mark as new pricing system
        updateField('pricingMode', 'HOURLY');
    };

    // Render backward compatible simple editor with upgrade option
    if (!hasNewPricing) {
        return (
            <div className={classes.mainContainer}>
                <div className={classes.textContainer}>
                    <h2 className={`${classes.title} titleSemibold`}>Preis & Stundenanzahl</h2>
                    <div className={classes.offerContainer}>
                        <div className={classes.offerIcon}>
                            <img src={offerIcon} alt="Offer Icon" />
                        </div>
                        <span className={classes.offerText}>
                            Als Stu Original zahlst du im Soft Launch 0% Provision.
                        </span>
                    </div>
                    <div style={{ marginTop: '16px' }}>
                        <button
                            type="button"
                            onClick={handleUpgradeToNewPricing}
                            className="semiboldSmall"
                            style={{
                                padding: '8px 16px',
                                backgroundColor: 'var(--dark-grey)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            Erweiterte Preisoptionen aktivieren
                        </button>
                        <p className="slimRegularSmallText" style={{ marginTop: '8px', color: 'var(--dark-grey-4)' }}>
                            Aktiviere Paketpreise, Rabatte, Wochenendaufschläge und mehr
                        </p>
                    </div>
                </div>
                <div className={classes.inputContainer}>
                    <div className={classes.inputWrapper} style={{width: window.innerWidth > 742 ? '' : '100%'}}>
                        <h3 className={`${classes.inputTitle} semiboldMiddle `}>Bestimme deinen Preis pro Stunde</h3>
                        <div className={classes.sizeContainer} style={{width: window.innerWidth > 742 ? '' : '100%'}}>
                            <input
                                style={{width: window.innerWidth > 742 ? '366px' : '100%'}}
                                className="slimRegularLarge"
                                type="number"
                                id="studioPrice"
                                placeholder="Preis in Euro"
                                name="pricePerHour"
                                value={formData.pricePerHour}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                min="10"
                                max="4000"
                                step="1"
                                pattern="[0-9]*"
                            />
                            <div className={classes.inputDescriptionWrapper}>
                                <span className={`${classes.inputDescription} slimRegularLargeText`}>€</span>
                            </div>
                        </div>
                        {errors.pricePerHour && <ErrorMessage style={{marginTop: "12px"}}>{errors.pricePerHour}</ErrorMessage>}
                    </div>
                    <div className={classes.peopleContainer} style={{width: window.innerWidth > 742 ? '' : '100%'}}>
                        <h3 className={`${classes.inputTitle} semiboldMiddle`}>Gibt es eine Mindeststundenanzahl?</h3>
                        <input
                            style={{width: window.innerWidth > 742 ? '366px' : '100%'}}
                            className="slimRegularLargeText"
                            type="number"
                            id="studioTime"
                            placeholder="Stunden (von 1 bis 12)"
                            name="minNumberOfHours"
                            value={formData.minNumberOfHours}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            min="1"
                            max="12"
                            step="1"
                            pattern="[0-9]*"
                        />
                        {errors.minNumberOfHours && <ErrorMessage>{errors.minNumberOfHours}</ErrorMessage>}
                    </div>
                </div>
            </div>
        );
    }

    // Render new pricing structure editor
    return (
        <div className={classes.mainContainer}>
            <div className={classes.textContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Preis & Stundenanzahl</h2>
                <span className="slimRegularLarge">Bestimme deinen Preis und wie viele Stunden ein Nutzer mindestens bei dir buchen muss.</span>
                <div className={classes.offerContainer}>
                    <div className={classes.offerIcon}>
                        <img src={offerIcon} alt="Offer Icon" />
                    </div>
                    <span className={classes.offerText}>
                        Als Stu Original zahlst du im Soft Launch 0% Provision.
                    </span>
                </div>
            </div>

            <div className={classes.casesContainerChoser}>
                <div
                    className={`${classes.caseBox} ${selectedPricingType === "stundenpreis" ? classes.caseBoxSelected : ""}`}
                    onClick={() => handlePricingTypeChange("stundenpreis")}
                >
                    {selectedPricingType === "stundenpreis" && (
                        <div className={classes.checkBox}>
                            <CheckIcon />
                        </div>
                    )}
                    <h3 className="semiboldMiddle">Stundenpreis</h3>
                    <p style={{color: "var(--dark-grey-4)"}} className="slimRegularLargeText">
                    Lege einen Preis pro Stunde fest. Ideal für flexible Buchungen.
                    </p>
                </div>
                <div
                    className={`${classes.caseBox} ${selectedPricingType === "paketpreise" ? classes.caseBoxSelected : ""}`}
                    onClick={() => handlePricingTypeChange("paketpreise")}
                >
                    {selectedPricingType === "paketpreise" && (
                        <div className={classes.checkBox}>
                            <CheckIcon />
                        </div>
                    )}
                    <h3 className="semiboldMiddle">Paketpreise</h3>
                    <p style={{color: "var(--dark-grey-4)"}} className="slimRegularLargeText">
                    Erstelle feste Pakete (z. B. Half-Day, Full-Day).
                    </p>
                </div>
            </div>

            <div className={`${classes.casesContainerChoserMobile} ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.displayRow : ""}`}>
                <div
                    className={`${classes.caseBox} ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.caseBoxCentered : ""}`}
                    onClick={() => handlePricingTypeChange("stundenpreis")}
                    style={{border: selectedPricingType === "stundenpreis" || selectedPricingType === "" ? "1.5px solid var(--dark-grey)" : "1.5px solid var(--light-grey-1)" }}
                >
                    <div className={`${classes.checkBoxMobile} ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.displayNone : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                        </svg>
                    </div>
                    <h3 className="semiboldMiddle">Stundenpreis</h3>
                    <p style={{color: "var(--dark-grey-4)"}} className={`slimRegularLargeText ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.displayNone : ""}`}>
                    Lege einen Preis pro Stunde fest. Ideal für flexible Buchungen.
                    </p>
                </div>
                <div
                    className={`${classes.caseBox} ${selectedPricingType === "paketpreise" || selectedPricingType === "stundenpreis" ? classes.caseBoxCentered : ""}`}
                    style={{border: selectedPricingType === "paketpreise" || selectedPricingType === "" ? "1.5px solid var(--dark-grey)" : "1.5px solid var(--light-grey-1)" }}
                    onClick={() => handlePricingTypeChange("paketpreise")}
                >
                    <div className={`${classes.checkBoxMobile} ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.displayNone : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                        </svg>
                    </div>
                    <h3 className="semiboldMiddle">Paketpreise</h3>
                    <p style={{color: "var(--dark-grey-4)"}} className={`slimRegularLargeText ${selectedPricingType === "stundenpreis" || selectedPricingType === "paketpreise" ? classes.displayNone : ""}`}>
                    Erstelle feste Pakete (z. B. Half-Day, Full-Day).
                    </p>
                </div>
            </div>

            {selectedPricingType === "stundenpreis" ? (
                <Stundenpreis validate={childValidateRef} />
            ) : selectedPricingType === "paketpreise" ? (
                <Paketpreise validate={childValidateRef} />
            ) : null}
        </div>
    );
};

export default PreisEditor;
